import React, { useState } from 'react';
import { Button } from '../ui/Button';
import { CheckIcon, ShieldCheckIcon, ClockIcon, CreditCardIcon, HeartIcon, BadgeCheckIcon, ArrowRightIcon, PhoneIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
export const Pricing = () => {
  const navigate = useNavigate();
  const openCalendly = () => {
    window.open('https://calendly.com/zerototendigital/exploration-feature-design', '_blank');
  };
  const plans = [{
    name: 'Starter',
    price: '$4,500',
    period: 'per month',
    hourlyRate: '$110/hour',
    hours: '10-12 hours/week',
    description: 'For early-stage teams',
    leadPmNote: 'Includes a dedicated Senior PM (your day-to-day partner)',
    aiNote: 'Includes AI-powered market analysis and prototype mockups',
    features: ['Product roadmapping', 'Sprint planning', 'Basic user research', 'Weekly check-ins', 'Email support'],
    popular: false,
    buttonText: 'Speak with a Senior PM',
    buttonIcon: <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-631" />,
    buttonVariant: 'secondary',
    onClick: openCalendly
  }, {
    name: 'Growth',
    price: '$8,500',
    period: 'per month',
    hourlyRate: '$98/hour',
    hours: '18-22 hours/week',
    description: 'For growing companies',
    leadPmNote: 'Includes a dedicated Senior PM (your day-to-day partner)',
    aiNote: 'In-depth AI/ML feature exploration, prototype development and testing',
    features: ['Full product strategy', 'Comprehensive roadmapping', 'In-depth user research', 'PRD development', '2x weekly check-ins', 'Priority support'],
    popular: true,
    fastAssignment: true,
    buttonText: 'Speak with a Senior PM',
    buttonIcon: <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-632" />,
    buttonVariant: 'primary'
  }, {
    name: 'Scale',
    price: '$15,999',
    period: 'per month',
    hourlyRate: '$100/hour',
    hours: '35-40 hours/week',
    description: 'For full lifecycle ownership',
    leadPmNote: 'Includes a dedicated Lead PM (your day-to-day partner)',
    aiNote: 'Full lifecycle AI product strategy, including advanced prototypes and user validation',
    features: ['Dedicated senior PM', 'Full product lifecycle management', 'Market & competitive analysis', 'Executive reporting', 'Team management', 'Daily standups', '24/7 priority support'],
    popular: false,
    buttonText: 'Speak with a Senior PM',
    buttonIcon: <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-633" />,
    buttonVariant: 'secondary'
  }, {
    name: 'Custom',
    price: 'Custom',
    period: 'pricing',
    hourlyRate: 'Tailored',
    hours: 'Flexible hours',
    description: 'For enterprise organizations',
    leadPmNote: 'Includes a dedicated Lead PM (your day-to-day partner)',
    aiNote: 'Tailored AI solutions and custom prototypes—integrated with your tech stack',
    features: ['Fully customized solutions', 'Dedicated PM team', 'Executive strategy sessions', 'Advanced AI integrations', 'Custom reporting dashboards', 'Strategic consulting', 'Priority access to new features', 'Dedicated account manager'],
    popular: false,
    buttonText: 'Speak with a Senior PM',
    buttonIcon: <PhoneIcon className="ml-2 h-4 w-4" data-id="element-634" />,
    buttonVariant: 'outline'
  }];
  return <section id="pricing" className="py-20 bg-white relative overflow-hidden" data-id="element-635">
      {/* Background pattern elements */}
      <div className="absolute top-0 right-0 w-72 h-72 bg-purple-50 rounded-full opacity-50 -mr-36 -mt-36" data-id="element-636"></div>
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-purple-50 rounded-full opacity-40 -ml-40 -mb-40" data-id="element-637"></div>
      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-638">
        <div className="text-center mb-12" data-id="element-639">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-black" data-id="element-640">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-text-secondary max-w-2xl mx-auto" data-id="element-641">
            Choose the plan that works for your business needs.
          </p>
        </div>
        {/* Guarantee Badge */}
        <div className="flex justify-center mb-10" data-id="element-642">
          <div className="bg-purple-50 rounded-full px-5 py-2 inline-flex items-center" data-id="element-643">
            <ShieldCheckIcon className="h-5 w-5 text-primary mr-2" data-id="element-644" />
            <span className="text-sm font-medium" data-id="element-645">
              Lead PM in 48h • AI & Prototype support included • Interview
              optional
            </span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto" data-id="element-646">
          {plans.map((plan, index) => <div key={index} className={`bg-white rounded-xl border ${plan.popular ? 'border-primary shadow-lg ring-2 ring-primary ring-opacity-20' : 'border-gray-200 shadow-sm hover:shadow-md'} transition-all duration-300 flex flex-col h-full relative`} data-id="element-647">
              {plan.popular && <div className="absolute -top-4 -right-4" data-id="element-648">
                  <div className="bg-primary text-white text-xs font-bold px-3 py-1 rounded-full shadow-md flex items-center" data-id="element-649">
                    <BadgeCheckIcon className="h-3 w-3 mr-1" data-id="element-650" />
                    MOST POPULAR
                  </div>
                </div>}
              {plan.fastAssignment && <div className="absolute -top-4 left-4" data-id="element-651"></div>}
              <div className="p-6 border-b border-gray-100" data-id="element-652">
                <h3 className="text-xl font-bold text-black mb-1" data-id="element-653">
                  {plan.name}
                </h3>
                <p className="text-sm text-gray-500 mb-2" data-id="element-654">{plan.description}</p>
                <p className="text-xs text-primary mb-2" data-id="element-655">{plan.leadPmNote}</p>
                <p className="text-xs text-green-600 mb-4" data-id="element-656">{plan.aiNote}</p>
                <div className="flex items-baseline mb-1" data-id="element-657">
                  <span className="text-3xl font-bold text-black" data-id="element-658">
                    {plan.price}
                  </span>
                  <span className="text-gray-500 ml-1 text-sm" data-id="element-659">
                    {plan.period}
                  </span>
                </div>
                <div className="flex flex-col space-y-2 mb-4" data-id="element-660">
                  <div className="flex items-center text-primary-dark font-medium" data-id="element-661">
                    <ClockIcon className="w-4 h-4 mr-2" data-id="element-662" />
                    <span className="text-lg" data-id="element-663">{plan.hours}</span>
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-664">
                    Effective rate: {plan.hourlyRate}
                  </div>
                </div>
              </div>
              <div className="p-6 flex-grow" data-id="element-665">
                <ul className="space-y-3 mb-6" data-id="element-666">
                  {plan.features.map((feature, idx) => <li key={idx} className="flex items-start" data-id="element-667">
                      <div className="flex-shrink-0 mt-1" data-id="element-668">
                        <div className="w-4 h-4 rounded-full bg-primary bg-opacity-10 flex items-center justify-center" data-id="element-669">
                          <CheckIcon className="w-3 h-3 text-primary" data-id="element-670" />
                        </div>
                      </div>
                      <span className="text-gray-700 ml-3 text-sm" data-id="element-671">
                        {feature}
                      </span>
                    </li>)}
                </ul>
              </div>
              <div className="p-6 pt-0 mt-auto" data-id="element-672">
                <Button variant={plan.buttonVariant as any} className={`w-full ${plan.popular ? 'bg-primary text-white hover:bg-primary-dark' : ''}`} onClick={openCalendly} data-id="element-673">
                  {plan.buttonText}
                  {plan.buttonIcon}
                </Button>
              </div>
            </div>)}
        </div>
        {/* Our Guarantees Section */}
        <div className="mt-24 max-w-5xl mx-auto" data-id="element-674">
          <div className="text-center mb-12" data-id="element-675">
            <h3 className="text-2xl md:text-3xl font-bold text-black" data-id="element-676">
              Our Guarantees
            </h3>
            <p className="text-text-secondary mt-3" data-id="element-677">
              We stand behind our service with these promises
            </p>
            <div className="inline-block bg-green-50 text-green-600 px-4 py-2 rounded-full text-sm font-medium mt-4" data-id="element-678">
              AI & prototyping expertise guaranteed – if your PM isn't a fit,
              we'll swap or refund 75% within 14 days.
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-679">
            <div className="bg-purple-50 rounded-xl p-6 flex items-start" data-id="element-680">
              <div className="bg-white p-3 rounded-full mr-4 shadow-sm" data-id="element-681">
                <ShieldCheckIcon className="w-6 h-6 text-primary" data-id="element-682" />
              </div>
              <div data-id="element-683">
                <h4 className="font-bold text-lg mb-2" data-id="element-684">
                  Satisfaction Guarantee
                </h4>
                <p className="text-text-secondary text-sm" data-id="element-685">
                  If you're not satisfied within the first 14 days, we'll refund
                  75% of your subscription – no questions asked.{' '}
                  <a href="/refund-policy" className="text-primary hover:underline" data-id="element-686">
                    View our full policy
                  </a>
                  .
                </p>
              </div>
            </div>
            <div className="bg-purple-50 rounded-xl p-6 flex items-start" data-id="element-687">
              <div className="bg-white p-3 rounded-full mr-4 shadow-sm" data-id="element-688">
                <ClockIcon className="w-6 h-6 text-primary" data-id="element-689" />
              </div>
              <div data-id="element-690">
                <h4 className="font-bold text-lg mb-2" data-id="element-691">Quick Onboarding</h4>
                <p className="text-text-secondary text-sm" data-id="element-692">
                  Get matched with your PM and start working within 48 hours. No
                  lengthy hiring processes.
                </p>
              </div>
            </div>
            <div className="bg-purple-50 rounded-xl p-6 flex items-start" data-id="element-693">
              <div className="bg-white p-3 rounded-full mr-4 shadow-sm" data-id="element-694">
                <CreditCardIcon className="w-6 h-6 text-primary" data-id="element-695" />
              </div>
              <div data-id="element-696">
                <h4 className="font-bold text-lg mb-2" data-id="element-697">Flexible Billing</h4>
                <p className="text-text-secondary text-sm" data-id="element-698">
                  Change or cancel your plan anytime. Scale up during busy
                  periods and scale down when needed.
                </p>
              </div>
            </div>
            <div className="bg-purple-50 rounded-xl p-6 flex items-start" data-id="element-699">
              <div className="bg-white p-3 rounded-full mr-4 shadow-sm" data-id="element-700">
                <HeartIcon className="w-6 h-6 text-primary" data-id="element-701" />
              </div>
              <div data-id="element-702">
                <h4 className="font-bold text-lg mb-2" data-id="element-703">Quality Commitment</h4>
                <p className="text-text-secondary text-sm" data-id="element-704">
                  All our PMs have 5+ years of experience with AI and
                  prototyping at top tech companies. We only work with the best.
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Get Started Section */}
        <div className="mt-24 mb-8 max-w-4xl mx-auto bg-gradient-to-r from-primary to-primary-light rounded-2xl overflow-hidden shadow-lg" data-id="element-705">
          <div className="px-8 py-12 text-center" data-id="element-706">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4" data-id="element-707">
              Ready to supercharge your product with AI?
            </h3>
            <p className="text-white text-opacity-90 mb-8 max-w-2xl mx-auto" data-id="element-708">
              Join our roster of clients who trust us with their product
              management needs.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-709">
              <Button variant="secondary" className="bg-white text-primary hover:bg-gray-100 border-0" onClick={openCalendly} data-id="element-710">
                Speak with a Senior PM
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
};